import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Window from '../components/window-system/Window';
import UserInfo from '../components/modules/UserInfo/UserInfo';
import BattleRequestWindow from '../components/BattleRequestWindow/BattleRequestWindow';
import BattleResultModal from '../components/BattleResultModal/BattleResultModal';
import BattleWindow from '../components/BattleWindow/BattleWindow';
import BattleLogWindow from '../components/BattleLogWindow/BattleLogWindow';
import ChatWindow from '../components/ChatWindow';
import GameMap from '../components/GameMap/GameMap';
import { useGame } from '../contexts/GameContext';
import { gameSocket } from '../lib/socket';
import { battleSocket } from '../lib/battleSocket';
import logger from '../lib/logger';
import './GameScreen.css';

interface WindowState {
  id: string;
  title: string;
  component: React.ReactNode;
  position: { x: number; y: number };
  size: { width: number; height: number };
  isOpen: boolean;
  zIndex: number;
  contentClassName?: string;
}

const GameScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, logout, closeBattleResult, showNotification } = useGame();
  const { character, battleResult } = state;
  
  const WINDOWS_SETTINGS_KEY = character ? `windows_settings_${character.id}` : 'windows_settings_default';
  
  // Используем lazy initialization - функция вызовется только один раз при создании компонента
  const [windows, setWindows] = useState<WindowState[]>(() => {
    const defaultWindows: WindowState[] = [
      {
        id: 'user-info',
        title: 'Персонаж',
        component: <UserInfo />,
        position: { x: 20, y: 20 },
        size: { width: 320, height: 500 },
        isOpen: true,
        zIndex: 1000,
        contentClassName: 'no-scroll'
      },
      {
        id: 'battle-requests',
        title: 'Заявки на бой',
        component: character ? (
          <BattleRequestWindow
            characterId={character.id}
            characterNickname={character.nickname}
          />
        ) : null,
        position: { x: 400, y: 100 },
        size: { width: 600, height: 400 },
        isOpen: false,
        zIndex: 1001,
        contentClassName: 'no-scroll'
      },
      {
        id: 'battle-log',
        title: 'Лог боя',
        component: null, // Будет обновлено позже
        position: { x: window.innerWidth - 420, y: 20 },
        size: { width: 400, height: 500 },
        isOpen: false,
        zIndex: 1900,
        contentClassName: 'no-scroll'
      },
      {
        id: 'chat',
        title: 'Чат',
        component: <ChatWindow />,
        position: { x: 20, y: window.innerHeight - 420 },
        size: { width: 500, height: 400 },
        isOpen: false,
        zIndex: 1002,
        contentClassName: 'no-scroll'
      }
    ];

    try {
      const saved = localStorage.getItem(WINDOWS_SETTINGS_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Обновляем дефолтные окна сохраненными настройками
        return defaultWindows.map(defaultWindow => {
          const savedWindow = parsed.find((s: any) => s.id === defaultWindow.id);
          if (savedWindow) {
            return {
              ...defaultWindow,
              position: savedWindow.position,
              size: savedWindow.size,
              isOpen: savedWindow.isOpen,
              zIndex: savedWindow.zIndex
            };
          }
          return defaultWindow;
        });
      }
    } catch (e) {
      logger.error('GameScreen', 'Error loading settings:', e);
    }
    
    return defaultWindows;
  });
  
  const [highestZIndex, setHighestZIndex] = useState(() => {
    const maxZ = Math.max(...windows.map(w => w.zIndex));
    return maxZ > 0 ? maxZ : 1000;
  });
  
  // Флаг для предотвращения сохранения при первом рендере
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Состояние для отображения окна боя
  const [showBattleWindow, setShowBattleWindow] = useState(false);
  const [battleLog, setBattleLog] = useState<any[]>([]);
  const [battleOpponent, setBattleOpponent] = useState<any>(null);

  // Сохраняем ссылку на showNotification
  const showNotificationRef = React.useRef(showNotification);
  useEffect(() => {
    showNotificationRef.current = showNotification;
  }, [showNotification]);

  // Обработка активного боя при входе - слушаем custom событие
  useEffect(() => {
    const handleActiveBattle = (event: Event) => {
      const customEvent = event as CustomEvent;
      const data = customEvent.detail;
      console.log('!!! ACTIVE BATTLE EVENT RECEIVED !!!', data);
      logger.info('GameScreen', 'Active battle detected:', data.battleId);
      showNotificationRef.current('info', data.message);
      
      // Открываем окно боя
      console.log('Setting showBattleWindow to true');
      setBattleLog([]);
      setBattleOpponent(null);
      setShowBattleWindow(true);
      
      // Открываем окно лога и делаем его активным
      setWindows(prev => prev.map(w => {
        if (w.id === 'battle-log') {
          return { ...w, isOpen: true };
        }
        return w;
      }));
      
      // Увеличиваем z-index для окна лога
      setHighestZIndex(prev => {
        const newZIndex = prev + 1;
        setWindows(windows => windows.map(w => 
          w.id === 'battle-log' ? { ...w, zIndex: newZIndex } : w
        ));
        return newZIndex;
      });
    };

    console.log('Setting up character-active-battle listener');
    window.addEventListener('character-active-battle', handleActiveBattle);

    return () => {
      console.log('Removing character-active-battle listener');
      window.removeEventListener('character-active-battle', handleActiveBattle);
    };
  }, []); // Теперь нет зависимостей

  useEffect(() => {
    setIsInitialized(true);
  }, []);

  // Обработка события начала боя
  useEffect(() => {
    let battleSocketUnsubscribers: (() => void)[] = [];

    const handleBattleStarted = async (data: { battleId: string; redirectTo: string }) => {
      logger.info('GameScreen', 'Battle started:', data.battleId);
      setBattleLog([]); // Очищаем лог перед новым боем
      setBattleOpponent(null); // Очищаем информацию о противнике
      setShowBattleWindow(true);
      // Открываем окно лога
      setWindows(prev => prev.map(w => w.id === 'battle-log' ? { ...w, isOpen: true } : w));
      handleFocusWindow('battle-log');

      // Подписываемся на события боя только после его начала
      const handleBattleRestored = (data: any) => {
        setBattleOpponent(data.opponentCharacter);
      };

      const handleRoundResult = (result: any) => {
        logger.info('GameScreen', 'Round result received, adding to log:', result.events);
        setBattleLog(prev => [...prev, ...result.events]);
      };

      const handleBattleFinished = (data: { winner: string }) => {
        logger.info('GameScreen', 'Battle finished, cleaning up battleSocket');
        // Отписываемся от всех событий при окончании боя
        battleSocketUnsubscribers.forEach(unsub => unsub());
        battleSocketUnsubscribers = [];
        // Отключаем battleSocket после окончания боя
        if (battleSocket.isConnected()) {
          battleSocket.disconnect();
        }
      };

      // Сохраняем функции отписки
      battleSocketUnsubscribers.push(
        battleSocket.on('battle.restored', handleBattleRestored),
        battleSocket.on('battle.round-result', handleRoundResult),
        battleSocket.on('battle.finished', handleBattleFinished)
      );
    };

    gameSocket.on('battle.started', handleBattleStarted);

    return () => {
      gameSocket.off('battle.started', handleBattleStarted);
      // Отписываемся от всех событий battleSocket при размонтировании
      battleSocketUnsubscribers.forEach(unsub => unsub());
      // Отключаем battleSocket если он подключен
      if (battleSocket.isConnected()) {
        battleSocket.disconnect();
      }
    };
  }, []);

  // Обработка результата боя из location state
  useEffect(() => {
    if (location.state?.battleResult) {
      const { isWinner, experienceGained } = location.state.battleResult;
      if (isWinner) {
        showNotification('success', `Победа! Вы получили ${experienceGained} опыта!`);
      } else {
        showNotification('info', 'Вы проиграли. Попробуйте еще раз!');
      }
      // Очищаем state после показа уведомления
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, showNotification]);

  // Обновляем компоненты окон при изменении данных
  useEffect(() => {
    if (character) {
      setWindows(prev => prev.map(window => {
        if (window.id === 'battle-requests') {
          return {
            ...window,
            component: (
              <BattleRequestWindow
                characterId={character.id}
                characterNickname={character.nickname}
              />
            )
          };
        }
        if (window.id === 'battle-log') {
          return {
            ...window,
            component: (
              <BattleLogWindow
                logs={battleLog}
                characterId={character.id}
                opponentNickname={battleOpponent?.nickname}
              />
            )
          };
        }
        if (window.id === 'chat') {
          // Обновляем заголовок окна чата с названием локации
          const locationId = character.current_location_id;
          let locationName = 'Неизвестная локация';
          
          if (locationId) {
            if (locationId.type === 'traveling') {
              locationName = 'В пути';
            } else if (locationId.type === 'in_location') {
              const location = state.locations.find(loc => loc.id === locationId.location_id);
              locationName = location?.name || 'Неизвестная локация';
            }
          }
          
          return {
            ...window,
            title: `Чат - ${locationName}`
          };
        }
        return window;
      }));
    }
  }, [character, battleLog, battleOpponent, state.locations]);

  // Сохраняем настройки при изменении (но не при первом рендере)
  useEffect(() => {
    if (isInitialized) {
      const settings = windows.map(w => ({
        id: w.id,
        position: w.position,
        size: w.size,
        isOpen: w.isOpen,
        zIndex: w.zIndex
      }));
      localStorage.setItem(WINDOWS_SETTINGS_KEY, JSON.stringify(settings));
    }
  }, [windows, WINDOWS_SETTINGS_KEY, isInitialized]);

  const handleCloseWindow = (windowId: string) => {
    setWindows(prev => prev.map(window => 
      window.id === windowId ? { ...window, isOpen: false } : window
    ));
  };

  const handlePositionChange = (windowId: string, position: { x: number; y: number }) => {
    setWindows(prev => prev.map(window => 
      window.id === windowId ? { ...window, position } : window
    ));
  };

  const handleSizeChange = (windowId: string, size: { width: number; height: number }) => {
    setWindows(prev => prev.map(window => 
      window.id === windowId ? { ...window, size } : window
    ));
  };

  const handleFocusWindow = (windowId: string) => {
    const newZIndex = highestZIndex + 1;
    setHighestZIndex(newZIndex);
    setWindows(prev => prev.map(window => 
      window.id === windowId ? { ...window, zIndex: newZIndex } : window
    ));
  };

  const openBattleRequestWindow = () => {
    setWindows(prev => prev.map(w => 
      w.id === 'battle-requests' ? { ...w, isOpen: true } : w
    ));
    handleFocusWindow('battle-requests');
  };

  const toggleWindow = (windowId: string) => {
    setWindows(prev => prev.map(w => {
      if (w.id === windowId) {
        const newIsOpen = !w.isOpen;
        if (newIsOpen) {
          // При открытии окна, делаем его активным
          handleFocusWindow(windowId);
        }
        return { ...w, isOpen: newIsOpen };
      }
      return w;
    }));
  };

  const handleCloseBattleWindow = useCallback(() => {
    setShowBattleWindow(false);
    // Закрываем и окно лога
    setWindows(prev => prev.map(w => w.id === 'battle-log' ? { ...w, isOpen: false } : w));
    // Очищаем данные боя
    setBattleLog([]);
    setBattleOpponent(null);
  }, []);

  // Проверка после всех хуков
  if (!character) {
    return (
      <div className="game-screen">
        <div className="game-background">
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100vh',
            color: 'white'
          }}>
            <div>
              <div className="loading-spinner"></div>
              <p>Загрузка персонажа...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="game-screen">
      <div className="game-background">
        <GameMap />
      </div>
      
      <div className="game-toolbar">
        <button 
          className={`toolbar-btn ${windows.find(w => w.id === 'user-info')?.isOpen ? 'active' : ''}`}
          onClick={() => toggleWindow('user-info')}
        >
          👤 Персонаж
        </button>
        <button 
          className={`toolbar-btn ${windows.find(w => w.id === 'chat')?.isOpen ? 'active' : ''}`}
          onClick={() => toggleWindow('chat')}
        >
          💬 Чат
        </button>
        <button className="toolbar-btn" disabled>🎒 Инвентарь</button>
        <button 
          className={`toolbar-btn ${windows.find(w => w.id === 'battle-requests')?.isOpen ? 'active' : ''}`}
          onClick={() => toggleWindow('battle-requests')}
        >
          ⚔️ Бой
        </button>
        <button className="toolbar-btn" disabled>🗺️ Карта</button>
        <button className="toolbar-btn" disabled>📊 Статистика</button>
        
        <div style={{ marginLeft: 'auto' }}>
          <button 
            className="toolbar-btn logout-btn"
            onClick={logout}
          >
            🚪 Выход
          </button>
        </div>
      </div>

      <div className="windows-container">
        {windows.filter(w => w.isOpen && w.id !== 'battle-log').map(window => (
          <Window
            key={window.id}
            id={window.id}
            title={window.title}
            position={window.position}
            size={window.size}
            onClose={() => handleCloseWindow(window.id)}
            onFocus={() => handleFocusWindow(window.id)}
            onPositionChange={(pos) => handlePositionChange(window.id, pos)}
            onSizeChange={(size) => handleSizeChange(window.id, size)}
            zIndex={window.zIndex}
            contentClassName={window.contentClassName}
          >
            {window.component}
          </Window>
        ))}
      </div>
      
      {/* Оверлей затемнения для боя */}
      {showBattleWindow && (
        <div className="battle-overlay" />
      )}
      
      {/* Окно лога боя - отдельно, чтобы было выше оверлея */}
      {windows.filter(w => w.isOpen && w.id === 'battle-log').map(window => (
        <Window
          key={window.id}
          id={window.id}
          title={window.title}
          position={window.position}
          size={window.size}
          onClose={() => handleCloseWindow(window.id)}
          onFocus={() => handleFocusWindow(window.id)}
          onPositionChange={(pos) => handlePositionChange(window.id, pos)}
          onSizeChange={(size) => handleSizeChange(window.id, size)}
          zIndex={window.zIndex}
          contentClassName={window.contentClassName}
        >
          {window.component}
        </Window>
      ))}
      
      {/* Модальное окно результата боя */}
      {battleResult && character && (
        <BattleResultModal
          result={battleResult}
          characterId={character.id}
          onClose={closeBattleResult}
        />
      )}
      
      {/* Окно боя */}
      {showBattleWindow && character && (
        <BattleWindow
          onClose={handleCloseBattleWindow}
        />
      )}
    </div>
  );
};

export default GameScreen;
